import { useState, lazy, Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { OrderCalendar } from "@/components/OrderCalendar";
import { PushBell } from "@/components/PushBell";

const Overview = lazy(() => import("./admin/Overview"));
const Orders = lazy(() => import("./admin/Orders"));
const SavedSheets = lazy(() => import("./admin/SavedSheets"));
const DesignRequests = lazy(() => import("./admin/DesignRequests"));
const LabelStyles = lazy(() => import("./admin/Labels"));
const Reviews = lazy(() => import("./admin/Reviews"));
const Inventory = lazy(() => import("./admin/Inventory"));
const Placements = lazy(() => import("./admin/Placements"));
const Collections = lazy(() => import("./admin/Collections"));
const Designs = lazy(() => import("./admin/Designs"));
const GalleryAdmin = lazy(() => import("./admin/GalleryAdmin"));
const Coupons = lazy(() => import("./admin/Coupons"));
const Customers = lazy(() => import("./admin/Customers"));
const PaymentsReport = lazy(() => import("./admin/PaymentsReport"));
const Estimates = lazy(() => import("./admin/Estimates"));
const Outbox = lazy(() => import("./admin/Outbox"));
const Outstanding = lazy(() => import("./admin/Outstanding"));
const Invoices = lazy(() => import("./admin/Invoices"));
const Expenses = lazy(() => import("./admin/Expenses"));
const VendorAdmin = lazy(() => import("./admin/VendorAdmin"));
const QuoteInbox = lazy(() => import("./admin/QuoteInbox"));

const TabFallback = () => (
  <p className="text-zinc-500 font-mono-p mt-6" data-testid="admin-tab-loading">Loading…</p>
);

const TABS = [
  ["overview", "Overview"], ["orders", "Orders"], ["sheets", "Gang Sheets"],
  ["designreq", "Design Requests"], ["quotes", "Quotes"], ["labels", "Labels"], ["reviews", "Reviews"],
  ["calendar", "Calendar"], ["inventory", "Inventory"], ["placements", "Placements"],
  ["collections", "Collections"], ["designs", "Catalog"], ["vendor", "Vendor Market"], ["gallery", "Gallery"],
  ["coupons", "Promo Codes"], ["customers", "Customers"], ["payments", "Payments"], ["outstanding", "Outstanding"], ["invoices", "Invoices"], ["expenses", "Expenses"], ["estimates", "Estimates"], ["outbox", "Outbox"],
];

export default function Admin() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const { data: outbox } = useQuery({ queryKey: ["outbox"], queryFn: async () => (await api.get("/admin/outbox")).data, refetchInterval: 30000 });
  const heldCount = outbox?.held?.length || 0;
  const { data: newQuotes } = useQuery({ queryKey: ["admin-quotes", "new"], queryFn: async () => (await api.get("/admin/quotes?status=new")).data, refetchInterval: 30000 });
  const quoteCount = newQuotes?.length || 0;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-white">Command Center</h1>
          <p className="text-zinc-400 mt-1">Manage orders, inventory, catalog & customers.</p>
        </div>
        <PushBell admin />
      </div>
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-[#1e222d] border border-zinc-800 flex-wrap h-auto">
          {TABS.map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="data-[state=active]:bg-[#ff6a13] data-[state=active]:text-white">{l}{v === "outbox" && heldCount > 0 && <span data-testid="outbox-badge" title={`${heldCount} customer email${heldCount === 1 ? "" : "s"} waiting for your approval`} className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#ffd600] text-black text-[10px] font-bold animate-pulse">{heldCount > 99 ? "99+" : heldCount}</span>}{v === "quotes" && quoteCount > 0 && <span data-testid="quotes-badge" title={`${quoteCount} new quote request${quoteCount === 1 ? "" : "s"}`} className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#22c3d6] text-black text-[10px] font-bold">{quoteCount}</span>}</TabsTrigger>
          ))}
        </TabsList>
        <Suspense fallback={<TabFallback />}>
          <TabsContent value="overview"><Overview /></TabsContent>
          <TabsContent value="vendor"><VendorAdmin /></TabsContent>
          <TabsContent value="orders"><Orders /></TabsContent>
          <TabsContent value="payments"><PaymentsReport /></TabsContent>
          <TabsContent value="outstanding"><Outstanding /></TabsContent>
          <TabsContent value="invoices"><Invoices /></TabsContent>
          <TabsContent value="expenses"><Expenses /></TabsContent>
          <TabsContent value="estimates"><Estimates /></TabsContent>
          <TabsContent value="outbox"><Outbox /></TabsContent>
          <TabsContent value="sheets"><SavedSheets /></TabsContent>
          <TabsContent value="designreq"><DesignRequests /></TabsContent>
          <TabsContent value="quotes"><QuoteInbox /></TabsContent>
          <TabsContent value="labels"><LabelStyles /></TabsContent>
          <TabsContent value="reviews"><Reviews /></TabsContent>
          <TabsContent value="calendar"><OrderCalendar /></TabsContent>
          <TabsContent value="inventory"><Inventory /></TabsContent>
          <TabsContent value="placements"><Placements /></TabsContent>
          <TabsContent value="collections"><Collections /></TabsContent>
          <TabsContent value="designs"><Designs /></TabsContent>
          <TabsContent value="gallery"><GalleryAdmin /></TabsContent>
          <TabsContent value="coupons"><Coupons /></TabsContent>
          <TabsContent value="customers"><Customers /></TabsContent>
        </Suspense>
      </Tabs>
    </div>
  );
}
